import { useState, useEffect } from 'react';
import LoginService from '@/services/LoginService';

/**
 * Custom hook for validating password reset tokens
 * Returns minimal state and lets callers decide what to do with the response
 *
 * @param token - The password reset token
 * @param tokenId - The password reset token ID
 * @returns Object containing validation state
 */
const usePasswordResetLinkValidation = (token: string | null, tokenId: string | null) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    // If parameters are missing, stop validating and mark as invalid
    if (!token || !tokenId) {
      setIsValidating(false);
      setIsValid(false);
      return;
    }

    // Validate the token with the API
    const validateToken = async () => {
      try {
        setIsValidating(true);
        const response = await LoginService.validatePasswordResetToken(token, tokenId);

        // Check if the response indicates the token is valid
        const isTokenValid = response.data && response.data.valid === true;
        setIsValid(isTokenValid);
      } catch (error) {
        setIsValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, tokenId]);

  return {
    isValidating,
    isValid
  };
};

export default usePasswordResetLinkValidation;